import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import AudioUploader from "@/components/AudioUploader";

const empty = {
  title: "",
  description: "",
  cover_image: "",
  suno_url: "",
  audio_url: "",
  is_featured: true,
  sort_order: 0,
};

export default function AdminSongEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new" || !id;

  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/songs/${id}`);
        setForm({
          title: data.title || "",
          description: data.description || "",
          cover_image: data.cover_image || "",
          suno_url: data.suno_url || "",
          audio_url: data.audio_url || "",
          is_featured: !!data.is_featured,
          sort_order: data.sort_order || 0,
        });
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load song");
        navigate("/admin/songs");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/songs", form);
        toast.success("Song added");
        navigate(`/admin/songs/${data.id}`);
      } else {
        await api.put(`/admin/songs/${id}`, form);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-paper min-h-[80vh] flex items-center justify-center">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-song-editor">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin/songs"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-song-back-link"
        >
          <ArrowLeft size={14} /> Songs
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
          {isNew ? "Add a song" : "Edit song"}
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          {form.title || "New song"}
        </h1>

        <div className="mt-10 space-y-8">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Title
            </label>
            <input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              data-testid="song-title-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-2 font-serif-display text-2xl text-ink outline-none"
              placeholder="A Quiet Light"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Description (optional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              data-testid="song-description-input"
              rows={3}
              className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none italic"
              placeholder="A line or two about the song or the poem it grew from…"
            />
          </div>

          <ImageUploader
            value={form.cover_image}
            onChange={(v) => update("cover_image", v)}
            label="Cover image (optional)"
            testIdPrefix="song-cover"
            helper="Use cover art from Suno, or upload your own (max 8MB)."
          />

          <AudioUploader
            value={form.audio_url}
            onChange={(v) => update("audio_url", v)}
            testIdPrefix="song-audio"
          />

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Suno link (the share URL Suno gives you)
            </label>
            <input
              value={form.suno_url}
              onChange={(e) => update("suno_url", e.target.value)}
              data-testid="song-suno-url-input"
              type="url"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
              placeholder="https://suno.com/song/…"
            />
            <p className="text-xs text-ink-soft mt-2 italic">
              This becomes a &quot;Listen on Suno&quot; button so visitors can hear the
              song on Suno (and discover your other tracks).
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => update("is_featured", e.target.checked)}
                data-testid="song-featured-toggle"
                className="w-4 h-4"
              />
              <span className="text-sm text-ink">
                Featured (show on Listen page)
              </span>
            </label>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
                Sort order (lower = first)
              </label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => update("sort_order", parseInt(e.target.value || "0", 10))}
                data-testid="song-sort-input"
                className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
              />
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-rule pt-8 flex flex-wrap gap-4 justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="song-save-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : isNew ? "Add song" : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
}
